import { clamp } from '../../math/scalar';
import type { LayerStackSprite } from '../../render/layerstack/LayerStackSprite';
import type { InputSnapshot } from './InputSnapshot';

/**
 * Flies the veil-skimmer from an InputSnapshot.
 *
 * The position is kept as floats and only rounded on the way into the sprite,
 * so a slow diagonal still travels instead of losing its sub-pixel remainder
 * every tick. Speed is the single feel knob.
 */
const MOVE_SPEED_PX_PER_S = 210;
const CRUISE_STATE = 'cruise';
const FIRING_STATE = 'firing';
const MS_PER_SECOND = 1000;

export interface VeilSkimmerBounds {
  readonly width: number;
  readonly height: number;
}

/** Where the controller's `cruise` / `firing` intent goes when something else owns the sprite's state. */
export type VeilSkimmerStateSink = (state: 'cruise' | 'firing') => void;

export class VeilSkimmerController {
  private readonly bounds: VeilSkimmerBounds;
  private readonly stateSink: VeilSkimmerStateSink | null;
  private sprite: LayerStackSprite | null = null;
  private posX = 0;
  private posY = 0;

  constructor(opts: { bounds: VeilSkimmerBounds; stateSink?: VeilSkimmerStateSink }) {
    this.bounds = opts.bounds;
    this.stateSink = opts.stateSink ?? null;
  }

  /** Rebinds after the harness rebuilds the scene; the authored master faces left, so mirror starts false. */
  attachSprite(sprite: LayerStackSprite, spawnX: number, spawnY: number): void {
    this.sprite = sprite;
    this.posX = spawnX;
    this.posY = spawnY;
    sprite.setMirror(false);
    sprite.setState(CRUISE_STATE);
    this.applyPosition(sprite);
  }

  detachSprite(): void {
    this.sprite = null;
  }

  update(deltaMs: number, input: InputSnapshot): void {
    const sprite = this.sprite;
    if (!sprite) return;
    const step = (deltaMs / MS_PER_SECOND) * MOVE_SPEED_PX_PER_S;
    this.posX += input.moveX * step;
    this.posY += input.moveY * step;
    this.applyPosition(sprite);
    if (input.moveX > 0) sprite.setMirror(true);
    else if (input.moveX < 0) sprite.setMirror(false);
    const state = input.fire ? FIRING_STATE : CRUISE_STATE;
    // With a sink the ship's state has another writer (the FX damaged override);
    // writing the sprite here too would make the pair alternate every frame.
    if (this.stateSink) this.stateSink(state);
    else sprite.setState(state);
  }

  get x(): number {
    return this.posX;
  }

  get y(): number {
    return this.posY;
  }

  get mirror(): boolean {
    return this.sprite?.mirror ?? false;
  }

  get stateName(): string {
    return this.sprite?.stateName ?? '';
  }

  /** Clamped so the whole sprite stays inside the logical canvas; a sprite wider than the canvas pins to 0. */
  private applyPosition(sprite: LayerStackSprite): void {
    this.posX = clamp(this.posX, 0, Math.max(0, this.bounds.width - sprite.targetWidth));
    this.posY = clamp(this.posY, 0, Math.max(0, this.bounds.height - sprite.targetHeight));
    sprite.setPixelPosition(this.posX, this.posY);
  }
}
